import { orgAPI } from "@/api/api";
import type {
  ExecuteToolboxResponse,
  IntegrationConnection,
} from "@cohesive/api-client";
import type { GranolaMcpMeetingEntity } from "@cohesive/api-entities";
import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from "@tanstack/react-query";

function formatDate(d: Date): string {
  return d.toISOString().split("T")[0];
}

function defaultCustomStart(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  return formatDate(d);
}

function defaultCustomEnd(): string {
  return formatDate(new Date());
}

interface UseGranolaListMeetingsProps {
  connection: IntegrationConnection;
  customStart?: string;
  customEnd?: string;
  options?: Partial<
    UseInfiniteQueryOptions<
      ExecuteToolboxResponse<GranolaMcpMeetingEntity[]>,
      Error,
      InfiniteData<ExecuteToolboxResponse<GranolaMcpMeetingEntity[]>>
    >
  >;
}

export function useGranolaListMeetings({
  connection,
  customStart,
  customEnd,
  options,
}: UseGranolaListMeetingsProps) {
  const start = customStart || defaultCustomStart();
  const end = customEnd || defaultCustomEnd();

  return useInfiniteQuery({
    queryKey: ["granola-meetings", connection?.id, start, end],
    queryFn: async () => {
      const response = await orgAPI.toolbox.execute<
        GranolaMcpMeetingEntity[],
        Record<string, unknown>
      >({
        integration_connection_id: connection.id,
        action_name: "granola-mcp-list-meetings",
        action_input: {
          time_range: "custom",
          custom_start: start,
          custom_end: end,
        },
        entity_format: "both",
      });

      return response;
    },
    initialPageParam: {},
    getNextPageParam: () => undefined,
    enabled: !!connection?.id,
    staleTime: 1000 * 60,
    retry: 1,
    ...options,
  });
}
